import React, { useEffect, useRef } from "react";

// An infinite "galaxies within galaxies" canvas: nested spiral galaxies
// continuously emerge from the center, grow, rotate/evolve, and recycle —
// producing an endless cosmic zoom. Purely generative, no assets.
export default function GalaxyBackground({ className = "", opacity = 1, density = 1 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let w = 0, h = 0, dpr = Math.min(window.devicePixelRatio || 1, 2);

    const HUES = [45, 42, 38, 190, 205, 50]; // golds/ambers + cool cosmic blues
    const N = Math.max(5, Math.round(7 * density));
    const RANGE = 1;
    const MIN_SCALE = 0.02;
    const MAX_SCALE = 2.4;

    // Build galaxies with their own star systems
    const galaxies = [];
    const makeStars = () => {
      const arms = 2 + Math.floor(Math.random() * 3);
      const twist = 2.4 + Math.random() * 2.2;
      const count = 130 + Math.floor(Math.random() * 90);
      const stars = [];
      for (let i = 0; i < count; i++) {
        const arm = Math.floor(Math.random() * arms);
        const r = Math.pow(Math.random(), 0.6); // denser core
        const spread = (Math.random() - 0.5) * 0.5 * (1 - r * 0.5);
        const angle = (arm / arms) * Math.PI * 2 + r * twist + spread;
        stars.push({
          r,
          angle,
          size: 0.4 + Math.random() * 1.6,
          bright: 0.35 + Math.random() * 0.65,
          hueJit: (Math.random() - 0.5) * 16,
        });
      }
      return stars;
    };
    for (let i = 0; i < N; i++) {
      galaxies.push({
        offset: (i / N) * RANGE,
        rotation: Math.random() * Math.PI * 2,
        spin: (Math.random() < 0.5 ? -1 : 1) * (0.05 + Math.random() * 0.12),
        hue: HUES[i % HUES.length],
        px: (Math.random() - 0.5) * 0.12, // slight off-center drift
        py: (Math.random() - 0.5) * 0.12,
        stars: makeStars(),
      });
    }

    const resize = () => {
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    let depth = 0;
    let last = performance.now();

    const draw = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      depth = (depth + dt * 0.06) % RANGE;

      // background wash
      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = "#040406";
      ctx.fillRect(0, 0, w, h);

      const cx = w / 2, cy = h / 2;
      const baseRadius = Math.min(w, h) * 0.42;
      ctx.globalCompositeOperation = "lighter";

      // draw from far (large) to near for depth
      const items = galaxies
        .map((g) => {
          let t = (depth + g.offset) % RANGE;
          const scale = MIN_SCALE * Math.pow(MAX_SCALE / MIN_SCALE, t);
          // fade in as it emerges, fade out as it engulfs the screen
          const fadeIn = Math.min(1, t / 0.12);
          const fadeOut = Math.min(1, (1 - t) / 0.18);
          const alpha = Math.max(0, Math.min(1, fadeIn * fadeOut));
          return { g, t, scale, alpha };
        })
        .sort((a, b) => b.scale - a.scale);

      for (const { g, scale, alpha } of items) {
        if (alpha <= 0.001) continue;
        const gx = cx + g.px * baseRadius * scale * 0.4;
        const gy = cy + g.py * baseRadius * scale * 0.4;
        const rot = g.rotation + depth * g.spin * 40;
        const gr = baseRadius * scale;

        // core glow
        const coreR = gr * 0.5;
        const grad = ctx.createRadialGradient(gx, gy, 0, gx, gy, coreR);
        grad.addColorStop(0, `hsla(${g.hue}, 90%, 72%, ${0.5 * alpha * opacity})`);
        grad.addColorStop(0.4, `hsla(${g.hue}, 85%, 55%, ${0.18 * alpha * opacity})`);
        grad.addColorStop(1, `hsla(${g.hue}, 80%, 40%, 0)`);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(gx, gy, coreR, 0, Math.PI * 2);
        ctx.fill();

        // stars along spiral arms
        const cos = Math.cos(rot), sin = Math.sin(rot);
        for (const s of g.stars) {
          const rr = s.r * gr;
          const a = s.angle;
          let x = Math.cos(a) * rr;
          let y = Math.sin(a) * rr * 0.62; // slight ellipse
          const rx = x * cos - y * sin;
          const ry = x * sin + y * cos;
          const px = gx + rx, py = gy + ry;
          if (px < -20 || px > w + 20 || py < -20 || py > h + 20) continue;
          const size = s.size * Math.max(0.5, Math.min(2.2, scale));
          const a2 = s.bright * alpha * opacity * (1 - s.r * 0.35);
          ctx.fillStyle = `hsla(${g.hue + s.hueJit}, 95%, ${72 - s.r * 22}%, ${a2})`;
          ctx.beginPath();
          ctx.arc(px, py, size, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [opacity, density]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ display: "block", width: "100%", height: "100%" }}
      data-testid="galaxy-background"
    />
  );
}
